import { useEffect } from 'react';
import { __ } from '@wordpress/i18n';
import { format, setHours, setMinutes, setSeconds } from 'date-fns';
import { useBookingStore } from '../store/useBookingStore';
import { useBookingValidation } from '../hooks/useBookingValidation';
import {
	useService,
	useSelectedDate,
	useSelectedTime,
} from '../hooks/useBookingComputed';
import { formatCalendarDate } from '../utils/date-time';
import './ManualDateTimeInput.css';

export function ManualDateTimeInput() {
	const { data: service } = useService();
	const currentServiceId = useBookingStore((state) => state.currentServiceId);
	const selectedResourceIds = useBookingStore(
		(state) => state.selectedResourceIds
	);
	const excludeBookingId = useBookingStore((state) => state.excludeBookingId);

	const selectedDate = useSelectedDate();
	const selectedTime = useSelectedTime();
	const selectedRange = useBookingStore((state) => state.selectedRange);
	const setSelectedDate = useBookingStore((state) => state.setSelectedDate);
	const setSelectedTime = useBookingStore((state) => state.setSelectedTime);
	const setSelectedRange = useBookingStore((state) => state.setSelectedRange);

	const { warnings, isValidating, validateBooking, clearWarnings } =
		useBookingValidation();

	// Determine if this is a full-day booking (1440 minutes = 24 hours)
	const isFullDay = service ? service.blockSize >= 1440 : false;
	const allowsFlexibleDuration = service?.flexibleDuration ?? false;

	// For date ranges, use selectedRange.from as the start date
	const startDate = selectedDate || selectedRange?.from;
	const endDate = selectedRange?.to;

	// Validate when date/time changes
	useEffect(() => {
		if (!startDate) {
			clearWarnings();
			return;
		}

		// Skip validation for non-full-day bookings until time is entered
		if (!isFullDay && !selectedTime) {
			clearWarnings();
			return;
		}

		// Build start datetime
		const dateStr = formatCalendarDate(startDate);
		const timeStr = selectedTime || '00:00';
		const startDatetime = `${dateStr} ${timeStr}`;

		// Build end datetime only if range is set
		let endDatetime: string | undefined;
		if (endDate) {
			const endDateStr = formatCalendarDate(endDate);
			const endTimeStr = isFullDay ? '23:59' : format(endDate, 'HH:mm');
			endDatetime = `${endDateStr} ${endTimeStr}`;
		}

		// Validate
		validateBooking({
			serviceId: currentServiceId,
			startDatetime,
			endDatetime,
			resourceIds: selectedResourceIds,
			quantity: 1,
			excludeBookingId: excludeBookingId || undefined,
		});
	}, [
		startDate,
		endDate,
		selectedTime,
		isFullDay,
		currentServiceId,
		selectedResourceIds,
		excludeBookingId,
		validateBooking,
		clearWarnings,
	]);

	// Helper to format Date to input value (YYYY-MM-DD)
	const formatDateInput = (date: Date | undefined): string => {
		if (!date) {
			return '';
		}
		return formatCalendarDate(date);
	};

	// Helper to format time to input value (HH:MM)
	const formatTimeInput = (time: string | undefined): string => {
		if (!time) {
			return '';
		}
		// selectedTime is already in HH:MM format from store
		return time;
	};

	// Group warnings by severity
	const errorWarnings = warnings.filter((w) => w.severity === 'error');
	const otherWarnings = warnings.filter((w) => w.severity !== 'error');

	return (
		<div className="gpb-manual-input">
			<div className="gpb-manual-input__fields">
				<div className="gpb-manual-input__field">
					<label htmlFor="gpb-start-date">
						{__('Start Date', 'gp-bookings')} *
					</label>
					<input
						id="gpb-start-date"
						type="date"
						value={formatDateInput(startDate)}
						onChange={(e) => {
							const dateStr = e.target.value;
							if (!dateStr) {
								setSelectedDate(undefined);
								setSelectedRange(undefined);
								return;
							}

							// Parse as local date to avoid timezone issues
							// "2025-12-05" should be Dec 5 in local time, not UTC
							const [year, month, day] = dateStr
								.split('-')
								.map(Number);
							const date = new Date(year, month - 1, day);

							if (isFullDay && allowsFlexibleDuration) {
								// For date ranges, update the range
								setSelectedRange({
									from: date,
									to: endDate || date,
								});
							} else {
								// For single dates, update selectedDate
								setSelectedDate(date);
							}
						}}
						required
					/>
				</div>

				{!isFullDay && (
					<div className="gpb-manual-input__field">
						<label htmlFor="gpb-start-time">
							{__('Start Time', 'gp-bookings')} *
						</label>
						<input
							id="gpb-start-time"
							type="time"
							value={formatTimeInput(selectedTime)}
							onChange={(e) => {
								// Store as HH:MM format (like TimeSlots)
								const time = e.target.value || undefined;
								setSelectedTime(time);
							}}
							required
						/>
					</div>
				)}

				{allowsFlexibleDuration && (
					<>
						<div className="gpb-manual-input__field">
							<label htmlFor="gpb-end-date">
								{__('End Date', 'gp-bookings')}
							</label>
							<input
								id="gpb-end-date"
								type="date"
								min={
									startDate
										? formatDateInput(startDate)
										: undefined
								}
								value={formatDateInput(endDate)}
								onChange={(e) => {
									const dateStr = e.target.value;
									if (!dateStr) {
										setSelectedRange(undefined);
										return;
									}
									// Parse as local date and preserve existing time from endDate
									const [year, month, day] = dateStr
										.split('-')
										.map(Number);
									let newEndDate = new Date(
										year,
										month - 1,
										day
									);

									// Preserve time from existing endDate if it exists
									if (endDate) {
										newEndDate = setHours(
											newEndDate,
											endDate.getHours()
										);
										newEndDate = setMinutes(
											newEndDate,
											endDate.getMinutes()
										);
										newEndDate = setSeconds(newEndDate, 0);
									} else if (selectedTime) {
										// If no endDate yet, use start time
										const [hours, minutes] = selectedTime
											.split(':')
											.map(Number);
										newEndDate = setHours(
											newEndDate,
											hours
										);
										newEndDate = setMinutes(
											newEndDate,
											minutes
										);
										newEndDate = setSeconds(newEndDate, 0);
									}

									if (startDate) {
										setSelectedRange({
											from: startDate,
											to: newEndDate,
										});
									}
								}}
							/>
						</div>

						{!isFullDay && endDate && (
							<div className="gpb-manual-input__field">
								<label htmlFor="gpb-end-time">
									{__('End Time', 'gp-bookings')}
								</label>
								<input
									id="gpb-end-time"
									type="time"
									value={format(endDate, 'HH:mm')}
									onChange={(e) => {
										if (!e.target.value || !startDate) {
											return;
										}
										// Parse the time and update the end date with new time using date-fns
										const [hours, mins] = e.target.value
											.split(':')
											.map(Number);

										let newEndDate = new Date(endDate);
										newEndDate = setHours(
											newEndDate,
											hours
										);
										newEndDate = setMinutes(
											newEndDate,
											mins
										);
										newEndDate = setSeconds(newEndDate, 0);

										setSelectedRange({
											from: startDate,
											to: newEndDate,
										});
									}}
								/>
							</div>
						)}
					</>
				)}
			</div>

			{isValidating && (
				<div className="gpb-validating">
					{__('Validating…', 'gp-bookings')}
				</div>
			)}

			{warnings.length > 0 && !isValidating && (
				<div className="gpb-validation-warnings">
					{errorWarnings.length > 0 && (
						<div className="gpb-warnings-group gpb-warnings-group--error">
							<h4>{__('Critical Issues', 'gp-bookings')}</h4>
							{errorWarnings.map((warning, i) => (
								<div key={i} className="gpb-warning">
									{warning.message}
								</div>
							))}
						</div>
					)}

					{otherWarnings.length > 0 && (
						<div className="gpb-warnings-group gpb-warnings-group--warning">
							<h4>{__('Warnings', 'gp-bookings')}</h4>
							{otherWarnings.map((warning, i) => (
								<div key={i} className="gpb-warning">
									{warning.message}
								</div>
							))}
						</div>
					)}

					<div className="gpb-admin-override-note">
						<strong>{__('Admin Override:', 'gp-bookings')}</strong>{' '}
						{__('You can save this booking anyway.', 'gp-bookings')}
					</div>
				</div>
			)}
		</div>
	);
}
